import { AfterViewChecked, Component, OnDestroy, OnInit, ViewChild, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { faAutomobile, faBuilding, faPaperPlane, faUser } from '@fortawesome/free-solid-svg-icons';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from "rxjs/operators";
import { FileTypeConstants } from '../../../shared/constants/file-upload-type-constants';
import { DialogComponent } from '../../../shared/dialog/dialog.component';
import { FileUploadComponent } from '../../../shared/file-upload/file-upload.component';
import { UserCache } from "../services/user.cache";
import { ClaimService } from '../../claims/claim.service';
import { ClaimsAppCustomerComponent } from '../claims-app-customer/claims-app-customer.component';
import { ClaimsAppMechanicalBreakdownComponent } from '../claims-app-mechanical-breakdown/claims-app-mechanical-breakdown.component';
import { ClaimsAppConcernCauseComponentListComponent } from '../claims-app-concern-cause-component-list/claims-app-concern-cause-component-list.component';
import { ClaimsAppSubmissionMapper } from '../mappers/claims-app-submission.mapper';
import { ClaimsAppProgressStepComponent, ProgressStep } from '../claims-app-progress-step/claims-app-progress-step.component';
import { ClaimsAppRequest } from '../models/claims-app-request';
import { IRepairFacility } from '../services/models/repair-facility';
import { UserContact } from '../services/models/user-contact';
import { UserManager } from '../services/user.manager';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'claims-app-submission',
    templateUrl: './claims-app-submission.component.html',
    styleUrl: './claims-app-submission.component.scss',
})
export class ClaimsAppSubmissionComponent implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit {

    @ViewChild('customer') customer: ClaimsAppCustomerComponent;
    @ViewChild('mechanicalBreakdown') mechanicalBreakdown: ClaimsAppMechanicalBreakdownComponent;    
    @ViewChild('concernCausePartList') concernCausePartList: ClaimsAppConcernCauseComponentListComponent;
    @ViewChild('estimateAttachment') estimateAttachment: FileUploadComponent;
    @ViewChild('formScrollableContent') formScrollabeContent: Element;
    @ViewChild('progressFloat') progressFloat: ClaimsAppProgressStepComponent;
    
    public enableEstimateUi: boolean = environment.enableEstimateUi == "true";

    public repairFacility: IRepairFacility;
    public contact: UserContact;
    private readonly _destroying$ = new Subject<void>();

    public acceptedFiles: string[] = [FileTypeConstants.txt,
                                      FileTypeConstants.doc,
                                      FileTypeConstants.docx,
                                      FileTypeConstants.pdf,
                                      FileTypeConstants.jpeg,
                                      FileTypeConstants.jpg,
                                      FileTypeConstants.png,
                                      FileTypeConstants.PNG,
                                      FileTypeConstants.gif];

    public faSolidUser = faUser;
    public faSolidCar = faAutomobile
    public faSolidBuilding = faBuilding;
    public faSolidPaperPlane = faPaperPlane;

    public claimServiceResponseMessage: string;
    
    public modelDialog: MatDialogRef<DialogComponent>;
    
    // Indicates if the form has been submitted.
    public isFormSubmitted = false;
    private isSubmitting = false;
    public shouldScrollToTop = false;
    
    public claimServiceSubscription: Subscription;

    private readonly defaultSuccessMessage: string = 'Thank you for your submission. We will contact you soon to finalize your claim.';

    public progressSteps: ProgressStep[] = [
        { label: 'Customer', status: 'complete', color: '#8bc34a', sectionId: 'form-nav-customer' },
        { label: 'Mechanical Breakdown', status: 'active', color: '#e57373', sectionId: 'form-nav-mechanical-breakdown' },
        { label: 'Repair Facility', status: 'upcoming', color: '#fff', sectionId: 'form-nav-concern-entry' },
        { label: 'Review and Submit', status: 'upcoming', color: '#fff', sectionId: 'form-nav-submit' }
    ];
    public activeStepIndex = 1;

    // Track if sections have been validated for errors
    private customerValidated = false;
    private breakdownValidated = false;

    // TODO: Is there a better way to sum without nested looping?
    public get laborTotal(): number {
        let sum: number = 0;
        this.concernCausePartList?.objectForms?.forEach(concern =>
            concern.correctionEstimateList?.objectForms.forEach(correction =>
                correction.estimateList.laborForms?.forEach(estimate => sum += estimate.total)));
        return sum;
    }

    public get partTotal(): number {
        let sum: number = 0;
        this.concernCausePartList?.objectForms?.forEach(concern =>
            concern.correctionEstimateList?.objectForms.forEach(correction =>
                correction.estimateList.partForms?.forEach(estimate => sum += estimate.total)));
        return sum;
    }

    public get supplyTotal(): number {
        let sum: number = 0;
        this.concernCausePartList?.objectForms?.forEach(concern =>
            concern.correctionEstimateList?.objectForms.forEach(correction =>
                correction.estimateList.materialForms?.forEach(estimate => sum += estimate.total)));
        return sum;
    }

    constructor(private claimService: ClaimService,
                private userManager: UserManager,
                private userCache: UserCache,
                private dialog: MatDialog,
                private changeDetectorRef: ChangeDetectorRef) {
    }

    public get isLoading(): boolean {
        return this.userManager.updatingRepairFacilities || this.isSubmitting;
    }

    ngOnInit(): void {
        this.userCache.updated
                .pipe(takeUntil(this._destroying$))
                .subscribe(() => {
                    this.repairFacility = this.userCache.selectedRepairFacility;
                    this.contact = this.userCache.selectedContact;
                    this.updateProgress();
                });
    }

    ngAfterViewChecked(): void {
        // Scroll back to the top when resetting once the div is unhidden.
        if (this.shouldScrollToTop) {
            this.scrollToFirstSection();
            this.shouldScrollToTop = false;
        }
    }

    ngOnDestroy(): void {
        if (this.claimServiceSubscription) {
            this.claimServiceSubscription.unsubscribe();
        }
    }
    
    ngAfterViewInit(): void {
        if (this.customer?.form) {
            this.customer.form.valueChanges.subscribe(() => {
                this.updateProgress();
                this.changeDetectorRef.detectChanges();
            });
        }
        if (this.mechanicalBreakdown?.form) {
            this.mechanicalBreakdown.form.valueChanges.subscribe(() => {
                this.updateProgress();
                this.changeDetectorRef.detectChanges();
            });
        }
    }
    
    /**
     * Navigate to selected section.
     */ 
    public scrollToSection(sectionName: string) {
        const section = document.getElementById(sectionName);
        section.scrollIntoView({ behavior: 'smooth' });
    }
    
    /**
     * Navigate to selected first found section.
     */ 
    public scrollToFirstSection() {
        const firstSection = document.querySelector('section');
        firstSection.scrollIntoView({ behavior: 'smooth' });
    }
    
    /**
     * Confirm with the user that no estimated was attached.
     */ 
    private confirmEstimateUpload(): void {
        this.showDialog('Estimate confirmation',
            'Please note we need cause of failure and a repair estimate to authorize a claim. Are you sure you want to continue without uploading the estimate?',
            'Cancel',
            'Yes, Continue',
            () => this.submit(),
            () => {});
    }
    
    /**
     * Show confirmation dialog of provided message.
     */    
    public showDialog(title: string,
                      messageText: string,
                      cancelButtonText: string,
                      confirmationButtonText: string,
                      onConfirmEvent: () => void,
                      onCancelEvent: () => void): void {
        this.modelDialog = this.dialog.open(DialogComponent, {
            data: {
                title: title,
                bodyText: messageText,
                cancelButtonText: cancelButtonText,
                confirmationButtonText: confirmationButtonText
            }
        });
        this.modelDialog.afterClosed().subscribe(result => {
            if (result) onConfirmEvent();
            else onCancelEvent();
        });
    }
    
    /**
     * On submission of form.
     */
    public onSubmit(): void {
        if (this.validateAllRequiredData()) {
            // If no estimate is attached, verify with user.
            if (this.estimateAttachment.importFiles.length == 0) {
                this.confirmEstimateUpload();
            } else {
                this.submit();
            }
        }
    }

    /**
     * Submit claim to destination.
     */
    private submit(): void {
        const estimateFile: File = this.estimateAttachment.importFiles[0] ?? null;
        const request = this.buildRequest();

        this.saveClaim(request, estimateFile);
    }

    /**
     * Validate all forms in this component.
     */
    private validateAllRequiredData(): boolean {
        this.customer.validateForm();
        this.mechanicalBreakdown.validateForm();
        this.concernCausePartList.validateForm();

        this.updateProgress();

        return this.customer.formCompleted
            && this.mechanicalBreakdown.formCompleted
            && this.concernCausePartList.formCompleted;
    }

    /**
     * 
     */
    private saveClaim(request: ClaimsAppRequest, estimateFile: File) {
        if (request === null) {
            this.showDialog('Error with Request',
                            'There was an error submitting the claim. Please try again.',
                            'Ok',
                            undefined,
                            () => {},
                            () => {});
            return;
        }
        
        // If the form is already submitting, don't start a new request.
        if (this.isSubmitting) return;
        this.isSubmitting = true;
        
        this.claimServiceSubscription = this.claimService.saveClaim(estimateFile, request)
            .subscribe({
                next: response => {
                    this.claimServiceResponseMessage = response?.message ?? this.defaultSuccessMessage;
                    this.isFormSubmitted = true;
                    this.isSubmitting = false;
                },
                error: response => {
                    if (response?.error?.message) {
                        this.claimServiceResponseMessage = response?.error?.message ?? this.defaultSuccessMessage;
                        this.isFormSubmitted = true;
                    }
                    else {
                        // Assumes ProblemDetails.
                        this.showDialog(response?.error?.title ?? 'Error with Request',
                            response?.error?.detail ?? 'There was an error submitting the claim. Please try again.',
                            'Ok',
                            undefined,
                            () => { },
                            () => { });
                    }
                    this.isSubmitting = false;
                }
            });
    }

    /**
     * 
     */
    private buildRequest(): ClaimsAppRequest {
        const claimRequest = new ClaimsAppRequest();
        
        ClaimsAppSubmissionMapper.mapCustomerForm(claimRequest, this.customer.form);
        ClaimsAppSubmissionMapper.mapMechanicalBreakdownForm(claimRequest, this.mechanicalBreakdown.form);
        ClaimsAppSubmissionMapper.mapRepairFacilityInterface(claimRequest, this.repairFacility);
        ClaimsAppSubmissionMapper.mapContact(claimRequest, this.contact);
        
        ClaimsAppSubmissionMapper.mapConcernCausePartList(claimRequest, this.concernCausePartList);
        
        return claimRequest;
    }

    /**
     * Resets the form to its initial state.
     * @see {@link FormUtility.reset}
     */
    public reset(): void {
        this.customer.reset();
        this.mechanicalBreakdown.reset();
        this.concernCausePartList.reset();
        this.estimateAttachment.clearFiles();

        this.isFormSubmitted = false;
        this.shouldScrollToTop = true;
        this.customerValidated = false;
        this.breakdownValidated = false;
        
        this.updateProgress();
    }

    /**
     * Update the progress indicator based on form completion and repair facility selection.
     */
    private updateProgress(): void {
        const customerValid = this.customer?.form?.valid;
        const breakdownValid = this.mechanicalBreakdown?.form?.valid;
        const repairFacilityComplete = !!this.repairFacility;

        // Determine error state for each section
        const customerError = this.customerValidated && !customerValid;
        const breakdownError = this.breakdownValidated && !breakdownValid;
        
        // TODO: Move the colors to a style sheet for easier maintenance.
        // TODO: Move this toggling logic to the progress step component. Should be handled internally and not manually by consumer.
        const steps: ProgressStep[] = [
            { label: 'Customer', status: customerValid ? 'complete' : (customerError ? 'error' : 'active'), color: customerValid ? '#8bc34a' : (customerError ? '#e57373' : '#ffeb3b'), sectionId: 'form-nav-customer' },
            { label: 'Mechanical Breakdown', status: 'upcoming', color: '#fff', sectionId: 'form-nav-mechanical-breakdown' },
            { label: 'Repair Facility', status: 'upcoming', color: '#fff', sectionId: 'form-nav-concern-entry' },
            { label: 'Review and Submit', status: 'upcoming', color: '#fff', sectionId: 'form-nav-submit' }
        ];
        let activeIdx = 0;
        if (customerValid || customerError) {
            steps[0].status = customerValid ? 'complete' : (customerError ? 'error' : 'active');
            steps[0].color = customerValid ? '#8bc34a' : (customerError ? '#e57373' : '#ffeb3b');
            steps[1].status = breakdownValid ? 'complete' : (breakdownError ? 'error' : 'active');
            steps[1].color = breakdownValid ? '#8bc34a' : (breakdownError ? '#e57373' : '#ffeb3b');
            activeIdx = 1;
        }
        if ((customerValid || customerError) && (breakdownValid || breakdownError)) {
            steps[1].status = breakdownValid ? 'complete' : (breakdownError ? 'error' : 'active');
            steps[1].color = breakdownValid ? '#8bc34a' : (breakdownError ? '#e57373' : '#ffeb3b');
            steps[2].status = repairFacilityComplete ? 'complete' : 'active';
            steps[2].color = repairFacilityComplete ? '#8bc34a' : '#ffeb3b';
            activeIdx = 2;
        }
        if ((customerValid || customerError) && (breakdownValid || breakdownError) && repairFacilityComplete) {
            steps[2].status = repairFacilityComplete ? 'complete' : 'active';
            steps[2].color = repairFacilityComplete ? '#8bc34a' : '#ffeb3b';
            steps[3].status = 'active';
            steps[3].color = '#ffeb3b';
            activeIdx = 3;
        }
        this.progressSteps = steps;
        this.activeStepIndex = activeIdx;
        this.changeDetectorRef.detectChanges();
    }
}
